import { Loader2, Target } from 'lucide-react'
import { useEffect } from 'react'

import { useBookAiPurposeStore, useCurrentPurpose } from '@/store/useBookAiPurposeStore'

import { QuestionItem } from './question-item'

type Props = {
  episodeId: number
  sectionIndex: number
  /** 開いている問い（「章-種類-番号」） */
  openQuestion: string | null
  onOpenQuestion: (key: string) => void
  onNavigate: (key: number) => void
}

/**
 * 読む目的に沿った「読む前に頭に置く問い」（読む目的があるときだけ）
 *
 * 利用者ごと・章ごとに AI を呼ぶので、開いた章の分だけ作り、作ったものは使い回す。
 */
export const PurposeQuestions = ({ episodeId, sectionIndex, openQuestion, onOpenQuestion, onNavigate }: Props) => {
  const purpose = useCurrentPurpose(episodeId)
  const isLoading = useBookAiPurposeStore((state) => state.loadingQuestions.includes(sectionIndex))
  const load = useBookAiPurposeStore((state) => state.loadSectionQuestions)
  const purposeText = purpose?.purpose
  const questions = purpose?.section_questions[String(sectionIndex)]
  const hasMap = !!purpose?.map

  useEffect(() => {
    // 本文の埋め込みが無い（解析前）と作れないので、目的に効く箇所が出せるようになってから作る
    if (purposeText && hasMap) load(sectionIndex)
  }, [purposeText, hasMap, sectionIndex, load])

  if (!purposeText || !hasMap) return null
  return (
    <section className="space-y-1.5">
      <h3 className="flex items-center gap-1 text-xs font-semibold text-muted-foreground">
        <Target className="size-3.5 text-purple-500" />
        目的に沿った問い
      </h3>
      {isLoading && !questions ? (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          目的「{purposeText}」に合わせて問いを作っています…
        </p>
      ) : (
        <ul className="space-y-2">
          {(questions ?? []).map((question, i) => {
            const key = `${sectionIndex}-purpose-${i}`
            return (
              <QuestionItem
                key={key}
                episodeId={episodeId}
                sectionIndex={sectionIndex}
                question={question}
                isOpen={openQuestion === key}
                onOpen={() => onOpenQuestion(key)}
                onNavigate={onNavigate}
                icon={<Target className="mt-0.5 size-4 shrink-0 text-purple-500" />}
                className="bg-purple-500/5"
              />
            )
          })}
        </ul>
      )}
    </section>
  )
}
